import {AnswerStatus, DifficultySummaries, QuestionDifficulty, UserDto} from '@domain/pom'
import React, {useCallback, useMemo} from 'react'
import {NavigateFunction, useNavigate} from 'react-router'

import {CompletionCard} from '../components'
import {
  usePlantActions,
  usePomNavigation,
  useStringSearchParameter,
  useQuestionState,
  usePlantUpmId
} from '../hooks'
import {Section} from '../types'
import {countAllSubmissions, SubmissionSummary} from '../utils'

import {PlantSideCardContainer} from './PlantSideCardContainer'
import {QuestionListSideCardContainer} from './QuestionListSideCardContainer'
import {SectionTopContainer} from './SectionTopContainer'

type DashboardPlantSectionContainerProps = {
  sections: Section[]
  difficultyScoreSummaries: DifficultySummaries
  responsible?: UserDto
}

export const DashboardPlantSectionContainer = ({
  sections,
  responsible,
  difficultyScoreSummaries
}: DashboardPlantSectionContainerProps) => {
  const {data: actions, status} = usePlantActions()
  const plantId = usePlantUpmId()
  const nav = usePomNavigation()
  const navigate: NavigateFunction = useNavigate()
  const [, setQuestionId] = useStringSearchParameter('selectedQuestionId')

  const [{activeAnswerStatus, activeDifficulty}, {setActiveDifficulty, setActiveAnswerStatus}] =
    useQuestionState()

  const onScoreSectionClick = useCallback(
    (status?: AnswerStatus) => {
      setActiveAnswerStatus(status)
      setQuestionId(undefined)
    },
    [setActiveAnswerStatus, setQuestionId]
  )
  const onDifficultySectionClick = useCallback(
    (difficulty: QuestionDifficulty) => {
      setActiveDifficulty(difficulty)
      setQuestionId(undefined)
    },
    [setActiveDifficulty, setQuestionId]
  )

  const submissionSummary = useMemo<SubmissionSummary>(() => {
    return countAllSubmissions(sections, activeDifficulty)
  }, [sections, activeDifficulty])

  const onSectionRowClick = useCallback(
    (id: string) => {
      void navigate(nav.buildSectionPath(plantId, id))
    },
    [navigate, nav, plantId]
  )

  return (
    <SectionTopContainer
      actionLoadingStatus={status}
      isLoading={!actions || !sections}
      sideCardComponent={
        activeAnswerStatus ? (
          <QuestionListSideCardContainer difficulty={submissionSummary.difficulty} />
        ) : (
          <PlantSideCardContainer
            sections={sections}
            onSectionRowClick={onSectionRowClick}
            actions={(actions || []).filter((a) => a.status !== 'done')}
          />
        )
      }
    >
      <CompletionCard
        activeDifficulty={activeDifficulty}
        onScoreSectionClick={onScoreSectionClick}
        onDifficultySectionClick={onDifficultySectionClick}
        responsiblePerson={responsible}
        submissionSummary={submissionSummary}
        difficultySummaries={difficultyScoreSummaries}
      />
    </SectionTopContainer>
  )
}
